import { Component, OnInit, Input, AfterViewInit, HostListener, OnDestroy, Inject, PLATFORM_ID, ElementRef, TemplateRef } from '@angular/core';
import { BannerMeta } from '../../entity/banner-meta';
import { TemplateService } from '../../service/template.service';
import { Subject, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { GlobalComponentService } from '@techmore/global';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'banner-01',
  templateUrl: './banner-01.component.html',
  styleUrls: [
    './banner-01.component.scss',
    './video.scss',
  ]
})
export class Banner01Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() bannerMeta: BannerMeta[];
  /** 當前播放中的影音Dom */
  playingVideo: HTMLVideoElement;
  /**
   * 拜訪網頁後第一次的影音播放已成功，
   * 用在觸控式行動終端上判斷用戶已經觸碰過螢幕，可以當作與介面互動過，能夠自動播放影片的條件，
   * 若為假值，應監聽螢幕觸摸事件並完成第一次影片自動播放
   */
  firstPlayDone = false;
  /**
   * 組件重新渲染後的第一次播放已成功 (換頁後重回到首頁)
   * 當次渲染完組件後的第一支影片播放需顯示靜音提示
   */
  firstPlayAfterRendDone = false;
  /** 上次滾動的y軸高度 */
  protected lastPageYOffset = 0;
  /** 監聽頁面滾動可觀察物件 */
  protected windowScrollObs = new Subject<number>();
  /** 目前設備是否為可觸碰螢幕裝置 */
  isTouchScreen = false;
  /**
   * 輪播設定
   * https://getbootstrap.com/docs/3.3/javascript/#carousel-options
   */
  @Input() carouselOption: any;
  /** 是否顯示分頁點點，預設顯示 */
  @Input() dotted = true;
  /** 是否顯示左右換頁按鈕，預設顯示 */
  @Input() nav = true;
  /** 是否顯示圖片描述文字，預設為不顯示，若要顯示必須再版裡字形配置橫幅文字樣式 */
  @Input() showDesc = false;
  /** 圖片說明下的按鈕模板，辦須待一個參數接圖片描述資料 */
  @Input() descTmp: TemplateRef<any>;
  /**
   * 用戶是否允許聲音撥放，
   * 若用戶允許播放音效，則本次的網站拜訪首頁橫幅均允許播放音效
   */
  hasAllowSound = false;
  /** 顯示靜音提示 */
  isShowMuteTip = true;
  /** 現在輪播到的是影音 */
  isVideoCurrently: boolean;
  tagMaximumFontSize = 60;
  tagMinimumFontSize = 16;
  tagFontSizeGap = 1;
  isBrowser = false;
  carouselHost: HTMLElement;
  /** 目前活動中的 slide 索引 */
  activeSlideIndex = 0;

  constructor(
    protected templateService: TemplateService,
    protected globalComponentService: GlobalComponentService,
    @Inject(PLATFORM_ID) protected platformId: object,
    protected el: ElementRef,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.hasAllowSound = templateService.getHasAllowSoundPlay();
    this.firstPlayDone = templateService.getHasFirstVideoPlayed();
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.isTouchScreen = this.checkIsTouchMobile();
    }
  }

  checkIsTouchMobile(): boolean {
    let hasTouchScreen = false;
    const navigator: Navigator = window.navigator;
    if ('maxTouchPoints' in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else if ('msMaxTouchPoints' in navigator) {
      hasTouchScreen = (navigator as Navigator)['msMaxTouchPoints'] > 0;
    } else {
      const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        hasTouchScreen = !!mQ.matches;
      } else if ('orientation' in window) {
        hasTouchScreen = true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        const UA = (navigator as Navigator).userAgent;
        hasTouchScreen = (
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
        );
      }
    }
    return hasTouchScreen;
  }

  checkIsVideoCurrently() {
    const videoEl: HTMLVideoElement = this.carouselHost.querySelector('.active').querySelector('video') as HTMLVideoElement;
    if (videoEl) {
      this.isVideoCurrently = true;
    } else {
      this.isVideoCurrently = false;
    }
  }

  protected playCurrentVideo = () => {
    const videoEl: HTMLVideoElement = this.carouselHost.querySelector('.active').querySelector('video') as HTMLVideoElement;
    return new Promise<HTMLVideoElement>((resolve, reject) => {
      if (videoEl) {
        if (!this.hasAllowSound) {
          videoEl.muted = true;
        } else { videoEl.muted = false; }

        videoEl.play().then(() => {
          this.playingVideo = videoEl;
          resolve(videoEl);
        }).catch(reason => {
          reject(reason);
        });
      } else {
        resolve(undefined);
      }
    });
  }

  markFirstPlayDone = () => {
    this.firstPlayDone = true;
    this.templateService.setHasFirstVideoPlayed(true);
  }

  markThisRendFirstPlayDone = () => {
    this.firstPlayAfterRendDone = true;
    timer(8000).subscribe(t => {
      this.isShowMuteTip = false;
    });
    if (!this.firstPlayDone) {
      this.markFirstPlayDone();
    }
  }

  playCurrentVideo2 = () => {
    this.playCurrentVideo().then(vEl => {
      if (vEl && !this.firstPlayAfterRendDone) {
        this.markThisRendFirstPlayDone();
      }
    }).catch(error => console.error(error));
  }

  touchScreenInitPlayVideo = () => {
    this.playCurrentVideo().then(vEl => {
      if (vEl && !this.firstPlayAfterRendDone) {
        this.markThisRendFirstPlayDone();
      }
      window.document.body.removeEventListener('touchstart', this.touchScreenInitPlayVideo);
    });
  }

  cancelVideoMute() {
    this.playingVideo.muted = false;
    this.hasAllowSound = true;
    this.templateService.setHasAllowSoundPlay(true);
  }

  ngAfterViewInit(): void {
    this.carouselHost = (this.el.nativeElement as HTMLElement).querySelector('#banner_Carousel');
    if (this.isBrowser) {
      if (this.carouselOption) {
        $(this.carouselHost).carousel(this.carouselOption);
      } else {
        $(this.carouselHost).carousel();
      }

      this.checkIsVideoCurrently();

      if (this.isTouchScreen && !this.firstPlayDone) {
        window.document.body.addEventListener('touchstart', this.touchScreenInitPlayVideo);
      } else {
        if (this.isVideoCurrently) {
          this.playCurrentVideo2();
          (this.carouselHost.querySelector('.carousel-inner') as HTMLElement).style.maxHeight = 'unset';
        } else {
          (this.carouselHost.querySelector('.carousel-inner') as HTMLElement).style.maxHeight = '';
        }
      }

      $(this.carouselHost).on('slid.bs.carousel', ($queryEvent) => {
        if (this.playingVideo) {
          this.playingVideo.pause();
          this.playingVideo = undefined;
        }
        this.checkIsVideoCurrently();
        if (this.isVideoCurrently) {
          this.playCurrentVideo2();
          (this.carouselHost.querySelector('.carousel-inner') as HTMLElement).style.maxHeight = 'unset';
        } else {
          (this.carouselHost.querySelector('.carousel-inner') as HTMLElement).style.maxHeight = '';
        }

        const activeSlideEl = $(this.carouselHost).find('.active')[0];
        this.activeSlideIndex = +activeSlideEl.id.replace('banner_Carousel_slide_', '');
      });

      this.windowScrollObs.pipe(
        debounceTime(300)
      ).subscribe(pageYOffset => {
        const videoHeight = $(this.carouselHost).height();
        const direction = pageYOffset - this.lastPageYOffset; // + up, - down

        if (direction > 0 && pageYOffset > videoHeight + 50) {
          $(this.carouselHost).carousel('pause');
          try {
            if (this.playingVideo) {
              this.playingVideo.pause();
              this.playingVideo = undefined;
            }
          } catch (err) { }
        } else if (direction < 0 && pageYOffset < videoHeight + 50) {
          $(this.carouselHost).carousel('cycle');
          if (!this.playingVideo) {
            this.playCurrentVideo2();
          }
        }

        this.lastPageYOffset = pageYOffset;
      });

      document.body.addEventListener('focusin', this.handleFocusInEventHandler);
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      this.windowScrollObs.complete();
      document.body.removeEventListener('focusin', this.handleFocusInEventHandler);
      }
  }

  @HostListener('window:scroll', ['$event']) onScrollEvent($event) {
    this.windowScrollObs.next(window.pageYOffset);
  }
  
  /** 全局監聽焦點事件 */
  handleFocusInEventHandler = (event) => {
    const tg = event.target as HTMLElement;

    // 焦點在容器內
    let find = $(this.el.nativeElement).find(tg);
    if (find.length > 0) {
      $(this.carouselHost).carousel('pause');
      return;
    }

    // 焦點在開關和容器以外
    $(this.carouselHost).carousel('cycle');
  }

  /** 當影片或圖片資源載入 */
  onMediaSrcLoad(index: number) {}
}
